import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import './ReviewersList.css';

const ReviewersList = () => {
  const [reviewers, setReviewers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [filterRole, setFilterRole] = useState('all'); // 'all', 'Reviewer', 'Author'

  // Get filtered reviewers based on selected role
  const filteredReviewers = filterRole === 'all' 
    ? reviewers 
    : reviewers.filter(reviewer => {
        const role = reviewer.role?.toLowerCase();
        return role === filterRole.toLowerCase();
      });

  // Get counts for each role (case-insensitive)
  const reviewerCount = reviewers.filter(r => r.role?.toLowerCase() === 'reviewer').length;
  const authorCount = reviewers.filter(r => r.role?.toLowerCase() === 'author').length;
  const totalCount = reviewers.length;

  console.log('Counts:', { reviewerCount, authorCount, totalCount }); // Debug counts

  // Fetch the list of verified and approved reviewers
  const fetchReviewers = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_SERVER_URL}/api/v1/admin/approved-reviewers-list`);
      setReviewers(response.data);
      console.log('Fetched reviewers:', response.data); // Debug: Check the data structure
      console.log('Roles:', response.data.map(r => r.role)); // Debug: Check all roles
      setLoading(false);
    } catch (error) {
      console.error('Error fetching reviewers:', error);
      setError('Failed to load reviewers. Please try again.');
      setLoading(false);
    }
  };

  // Delete a reviewer
  const handleDelete = async (reviewerId) => {
    const result = await Swal.fire({
      title: 'Delete User?',
      text: "Are you sure you want to delete this user? This action cannot be undone!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#5e71f6',
      cancelButtonColor: '#ef4444',
      confirmButtonText: 'Yes, delete',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      setDeletingId(reviewerId);
      try {
        await axios.delete(`${process.env.REACT_APP_BACKEND_SERVER_URL}/api/v1/admin/reviewer/${reviewerId}`);
        
        setDeletingId(null);
        setReviewers(reviewers.filter(reviewer => reviewer._id !== reviewerId));
        
        Swal.fire({
          icon: 'success',
          title: 'Deleted!',
          text: 'User has been successfully deleted.',
          confirmButtonColor: '#5e71f6',
        });
      } catch (error) {
        setDeletingId(null);
        console.error('Error deleting user:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error.response?.data?.message || 'Failed to delete user. Please try again.',
          confirmButtonColor: '#5e71f6',
        });
      }
    }
  };

  useEffect(() => {
    fetchReviewers();
  }, []);

  if (loading) {
    return (
      <div className="paper-list-wrapper">
        <div className="reviewers-container">
          <div className="header-section">
            <h1 className="paper-list-title">User List</h1>
            <p className="paper-list-subtitle">Manage your approved users</p>
          </div>
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Loading users...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="paper-list-wrapper">
        <div className="reviewers-container">
          <div className="header-section">
            <h1 className="paper-list-title">Approved Users</h1>
            <p className="paper-list-subtitle">Manage your approved users</p>
          </div>
          <div className="error-container">
            <div className="error-icon">!</div>
            <p className="error-message">{error}</p>
            <button className="retry-button" onClick={fetchReviewers}>
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="paper-list-wrapper">
      <div className="reviewers-container">
        <div className="header-section">
          <h1 className="paper-list-title">Approved Users</h1>
          <p className="paper-list-subtitle">Manage your approved users</p>
        </div>

        {/* Filter and Count Section */}
        <div className="filter-section">
          <div className="filter-buttons">
            <button 
              className={`filter-btn ${filterRole === 'all' ? 'active' : ''}`}
              onClick={() => setFilterRole('all')}
            >
              <span className="filter-label">All Users</span>
              <span className="filter-count">{totalCount}</span>
            </button>
            <button 
              className={`filter-btn ${filterRole === 'Reviewer' ? 'active' : ''}`}
              onClick={() => setFilterRole('Reviewer')}
            >
              <span className="filter-label">Reviewers</span>
              <span className="filter-count">{reviewerCount}</span>
            </button>
            <button 
              className={`filter-btn ${filterRole === 'Author' ? 'active' : ''}`}
              onClick={() => setFilterRole('Author')}
            >
              <span className="filter-label">Authors</span>
              <span className="filter-count">{authorCount}</span>
            </button>
          </div>
        </div>

        {filteredReviewers.length === 0 ? (
          <div className="no-reviewers-box">
            <svg className="no-data-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" width="80" height="80">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            <h3>No Users Found</h3>
            <p>There are no {filterRole === 'all' ? 'approved users' : filterRole.toLowerCase() + 's'} in the system yet.</p>
          </div>
        ) : (
          <div className="reviewer-list">
            {filteredReviewers.map((reviewer) => (
              <div key={reviewer._id} className="reviewer-card">
                <div className="reviewer-info">
                  <div className="reviewer-header">
                    <h3 className="reviewer-name">
                      {reviewer.firstName} {reviewer.lastName}
                    </h3>
                    <span className="role-badge">
                      {reviewer.role}
                    </span>
                  </div>
                  <div className="reviewer-email">
                    <svg className="icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    {reviewer.email}
                  </div>
                </div>
                <button
                  className="btn-delete"
                  onClick={() => handleDelete(reviewer._id)}
                  disabled={deletingId === reviewer._id}
                >
                  {deletingId === reviewer._id ? (
                    <>
                      <div className="button-spinner"></div>
                      Deleting...
                    </>
                  ) : (
                    <>
                      <svg className="btn-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      Delete
                    </>
                  )}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReviewersList;
